<script setup lang="ts">
import { ref, watch } from 'vue';

import { useElementSize, useInfiniteScroll, useWindowSize } from '@vueuse/core';

import { useHeader } from '@/composables/useHeader';
import { useTransactions } from '@/composables/useTransactions';
import { useTransactionsView } from '@/composables/useTransactionsView';
import type { Transaction } from '@/references/axios/transactions/types';

import TransactionListItemOTC from '@/components/transaction-list-item/TransactionListItemOTC.vue';
import UiText from '@/components/ui/UiText.vue';

const emit = defineEmits<{
  (e: 'openDetails', payload: Transaction): void;
}>();

const { groups, showMore } = useTransactionsView();
const { load } = useTransactions();
const { height: windowHeight } = useWindowSize();
const { headerHeight } = useHeader();

const listElement = ref();
const { height: listHeight } = useElementSize(listElement);

function onExpire(): void {
  load();
}

useInfiniteScroll(
  window,
  () => {
    showMore();
  },
  { distance: 1000 }
);

watch(listHeight, () => {
  if (listHeight.value < windowHeight.value - headerHeight.value) {
    showMore();
  }
});
</script>

<template>
  <div class="transaction-history">
    <ul ref="listElement" class="transaction-history__list">
      <li v-for="group in groups" :key="group.id" class="transaction-history__group">
        <UiText class="transaction-history__group-title" tag="p" size="md" weight="medium" muted>
          {{ group.title }}
        </UiText>
        <ul class="transaction-history__group-items">
          <li
            v-for="item in group.items"
            :key="item.id"
            class="transaction-history__item"
            :tabindex="item.flags.openable ? 0 : undefined"
            :role="item.flags.openable ? 'button' : undefined"
            @keydown.enter="item.flags.openable ? emit('openDetails', item) : () => {}"
            @keydown.space.prevent="item.flags.openable ? emit('openDetails', item) : () => {}"
            @click="item.flags.openable ? emit('openDetails', item) : () => {}"
          >
            <TransactionListItemOTC :tx="item" @expire="onExpire" />
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.transaction-history__list {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

.transaction-history__group:not(:last-child) {
  margin: 0 0 24px;
}

.transaction-history__group-title {
  margin: 0 0 16px;
  padding: 0 16px;
}

.transaction-history__group-items {
  margin: 0;
  padding: 8px;
  list-style-type: none;
  background-color: var(--color-main-underlay);
  border-radius: 12px;
}

.transaction-history__item:not(:last-child) {
  margin: 0 0 8px;
}

.transaction-history__item[role='button'] {
  cursor: pointer;
}
</style>
